import { slideUp, slideDown } from '@globals/utils/functions';

class StickySecondaryNav {
	menu: HTMLElement | null;
	menuWrapper: HTMLElement | null;
	menuLinks: NodeListOf<HTMLElement>;
	headerHeight: string;
	blockHeight: number;
	offset: number;
	mobileTrigger: HTMLElement | null;
	anchors: HTMLElement[];
	breakpoints: IBreakpoints | undefined;
	isMobile: boolean;
	observer: IntersectionObserver | null = null;
	hasAdminBar: boolean;

	constructor(element: HTMLElement) {
		this.hasAdminBar = document.body.classList.contains('admin-bar');
		this.blockHeight = element.getBoundingClientRect().height;
		this.menu = element.querySelector<HTMLElement>('.block-sticky-secondary-nav__menu');
		this.menuWrapper = element.querySelector<HTMLElement>(
			'.block-sticky-secondary-nav__menu-wrapper'
		);
		this.menuLinks = element.querySelectorAll('.block-sticky-secondary-nav__menu-link');
		this.headerHeight = getComputedStyle(document.documentElement)
			.getPropertyValue('--headerHeightFull')
			.replace('px', '');
		this.offset =
			window.innerHeight -
			(parseInt(this.headerHeight) + this.blockHeight + (this.hasAdminBar ? 46 : 0) + 100); // 100px for buffer
		this.anchors = [];
		this.mobileTrigger = element.querySelector('.block-sticky-secondary-nav__mobile-trigger');
		this.breakpoints = window.impBreakpoints;
		this.isMobile = this.isMobileViewport();

		this.init();
	}

	init() {
		this.updateMeasurements();
		this.collectAnchors();
		this.setSecondaryNavHeight();
		this.setAriaAttributes();

		window.addEventListener('resize', this.resize.bind(this));

		if (this.mobileTrigger) {
			this.mobileTrigger.addEventListener('click', this.toggleMenu.bind(this));
		}

		this.menuLinks.forEach((link) => {
			link.addEventListener('click', () => {
				if (this.isMobileMenuOpen()) {
					this.closeMobileMenu();
				}
			});
		});

		this.setupIntersectionObserver();
	}

	resize() {
		const wasMobile = this.isMobile;
		this.updateMeasurements();
		this.isMobile = this.isMobileViewport();
		this.setSecondaryNavHeight();
		this.setAriaAttributes();
		this.setupIntersectionObserver();

		if (this.isMobile && !wasMobile && this.isMobileMenuOpen()) {
			this.closeMobileMenu();
		}
	}

	updateMeasurements() {
		this.headerHeight =
			getComputedStyle(document.documentElement)
				.getPropertyValue('--headerHeightFull')
				.replace('px', '') || '0';
		this.blockHeight =
			document.documentElement
				.querySelector('.block-sticky-secondary-nav')
				?.getBoundingClientRect().height || 0;
		this.offset =
			window.innerHeight -
			(parseInt(this.headerHeight) + this.blockHeight + (this.hasAdminBar ? 46 : 0) + 100);
		this.isMobile = this.isMobileViewport();
	}

	collectAnchors() {
		this.anchors = [];
		this.menuLinks.forEach((link) => {
			const href = link.getAttribute('href');
			if (href && href.startsWith('#') && href.length > 1) {
				const anchor = document.getElementById(href.slice(1));
				if (anchor) {
					this.anchors.push(anchor);
				}
			}
		});
	}

	setupIntersectionObserver() {
		if (this.observer) {
			this.observer.disconnect();
		}

		this.observer = new IntersectionObserver(
			(entries) => {
				const visibleAnchors: HTMLElement[] = [];
				entries.forEach((entry) => {
					if (entry.isIntersecting) {
						if (entry.target instanceof HTMLElement) {
							visibleAnchors.push(entry.target);
						}
					}
				});

				// Find the anchor closest to the top (last in visibleAnchors)
				let currentId = '';
				if (visibleAnchors.length > 0) {
					const sorted = visibleAnchors.sort(
						(a, b) => (a.offsetTop || 0) - (b.offsetTop || 0)
					);
					currentId = sorted[sorted.length - 1].id;
				}

				this.menuLinks.forEach((link) => {
					const href = link.getAttribute('href');
					if (href === `#${currentId}` && href !== '#') {
						link.classList.add('is-active');
					} else {
						link.classList.remove('is-active');
					}
				});
			},
			{
				rootMargin: `0px 0px -${this.offset}px 0px`,
				threshold: 0,
			}
		);

		this.anchors.forEach((anchor) => {
			if (this.observer) {
				this.observer.observe(anchor);
			}
		});
	}

	setSecondaryNavHeight() {
		document.documentElement.style.setProperty('--secondaryNavHeight', `${this.blockHeight}px`);
	}

	setAriaAttributes() {
		if (this.menuWrapper && this.mobileTrigger) {
			if (this.isMobile) {
				this.menuWrapper.setAttribute('aria-hidden', 'true');
				this.mobileTrigger.setAttribute('aria-expanded', 'false');
			} else {
				this.menuWrapper.setAttribute('aria-hidden', 'false');
				this.mobileTrigger.setAttribute('aria-expanded', 'true');
			}
		}
	}

	isMobileViewport(): boolean {
		const mediaQuery = this.breakpoints
			? `(max-width: ${parseInt(this.breakpoints.lg) - 1}px)`
			: '(max-width: 991px)';
		return window.matchMedia(mediaQuery).matches;
	}

	toggleMenu() {
		if (this.isMobileMenuOpen()) {
			this.closeMobileMenu();
		} else {
			this.openMobileMenu();
		}
	}

	isMobileMenuOpen(): boolean {
		if (this.menuWrapper) {
			return (
				this.menuWrapper.style.height === 'auto' &&
				this.menuWrapper.style.display === 'block'
			);
		}
		return false;
	}

	openMobileMenu() {
		if (this.mobileTrigger && this.menuWrapper) {
			this.mobileTrigger.setAttribute('aria-expanded', 'true');
			this.menuWrapper.setAttribute('aria-hidden', 'true');
			slideDown(this.menuWrapper, 400, 'block');
		}
	}

	closeMobileMenu() {
		if (this.mobileTrigger && this.menuWrapper) {
			this.mobileTrigger.setAttribute('aria-expanded', 'false');
			this.menuWrapper.setAttribute('aria-hidden', 'false');
			slideUp(this.menuWrapper, 400, 'block');
		}
	}
}

document.querySelectorAll<HTMLElement>('.block-sticky-secondary-nav').forEach((block) => {
	new StickySecondaryNav(block);
});
